import type {
  AdminSession,
  AccountSummary,
  ApiErrorBody,
  AuditRecord,
  BoardItem,
  BoardResponse,
  Channel,
  DuplicateIdea,
  IdeaInput,
  Job,
  PortalConfig,
  PublicationJob,
  RenderSubtask,
  ScriptProfile,
  Session,
  SettingsCatalog,
  ThroughputMetrics,
  Voice,
  Worker,
  WorkerRole,
  WordTargets,
} from "../types";

const DEFAULT_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api";
const CSRF_COOKIE = "swipecut_csrf";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  idempotencyKey?: string;
  csrf?: boolean;
}

type RawRow = Record<string, unknown>;

function text(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function nullableText(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function numeric(value: unknown, fallback = 0): number {
  if (typeof value === "number") return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function boolean(value: unknown): boolean {
  return value === true;
}

function nullableBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function object(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function stringArray<T extends string = string>(value: unknown): T[] {
  return Array.isArray(value) ? value.filter((item): item is T => typeof item === "string") : [];
}

function mapUser(row: RawRow) {
  return {
    id: text(row.id),
    email: text(row.email),
    displayName: text(row.displayName),
    role: text(row.role) as Session["user"]["role"],
    mustChangePassword: boolean(row.mustChangePassword),
    totpVerified: boolean(row.totpVerified),
  };
}

function mapSession(payload: RawRow): Session {
  const session = object(payload.session);
  return {
    id: text(session.id),
    user: mapUser(object(session.user)),
    csrfToken: text(session.csrfToken),
    expiresAt: text(session.expiresAt),
    totpEnrollmentRequired: boolean(payload.totpEnrollmentRequired),
  };
}

function mapBoardItem(row: RawRow): BoardItem {
  return {
    id: text(row.id),
    type: text(row.type) as BoardItem["type"],
    status: text(row.status) as BoardItem["status"],
    channelId: text(row.channel_id),
    channelName: text(row.channel_name),
    title: text(row.title),
    context: nullableText(row.context),
    suggestedDurationMinutes: row.suggested_duration_minutes === null
      || row.suggested_duration_minutes === undefined
      ? null
      : numeric(row.suggested_duration_minutes),
    publicationMode: nullableText(row.publication_mode) as BoardItem["publicationMode"],
    queuePosition: row.queue_position === null ? null : numeric(row.queue_position),
    createdBy: nullableText(row.created_by),
    createdAt: text(row.created_at),
    updatedAt: text(row.updated_at),
  };
}

function mapChannel(row: RawRow): Channel {
  return {
    id: text(row.id),
    name: text(row.name),
    slug: text(row.slug),
    trelloBoardId: nullableText(row.trello_board_id),
    defaultProfileId: nullableText(row.default_profile_id),
    defaultVoiceId: nullableText(row.default_voice_id),
    defaultDurationMinutes: numeric(row.default_duration_minutes, 15),
  };
}

function mapProfile(row: RawRow): ScriptProfile {
  return {
    id: text(row.id),
    name: text(row.name),
    version: numeric(row.version, 1),
    profile: object(row.profile),
    hash: text(row.profile_hash),
    isNeutral: boolean(row.is_neutral),
    active: boolean(row.active),
    createdAt: text(row.created_at),
    updatedAt: text(row.updated_at),
  };
}

function mapVoice(row: RawRow): Voice {
  return {
    id: text(row.id),
    name: text(row.name),
    providerKey: text(row.provider_key),
    settings: object(row.settings),
    active: boolean(row.active),
  };
}

function mapJob(row: RawRow): Job {
  return {
    id: text(row.id),
    ideaId: nullableText(row.idea_id),
    title: text(row.title),
    context: nullableText(row.context),
    existingScript: nullableText(row.existing_script),
    publicationMode: text(row.publication_mode, "normal") as Job["publicationMode"],
    channelId: text(row.channel_id),
    channelName: text(row.channel_name),
    status: text(row.status) as Job["status"],
    queuePosition: row.queue_position === null ? null : numeric(row.queue_position),
    durationMinutes: numeric(row.duration_minutes, 15),
    profileId: nullableText(row.profile_id),
    voiceId: nullableText(row.voice_id),
    driveUrl: nullableText(row.drive_url),
    youtubeUrl: nullableText(row.youtube_url),
    createdAt: text(row.created_at),
    updatedAt: text(row.updated_at),
    publishedAt: nullableText(row.published_at),
    currentAttemptId: nullableText(row.current_attempt_id),
    attemptNumber: row.attempt_number === null ? null : numeric(row.attempt_number),
    workerId: nullableText(row.worker_id),
    leaseExpiresAt: nullableText(row.lease_expires_at),
    failureCode: nullableText(row.attempt_failure_code ?? row.failure_code),
    failureDetail: nullableText(row.attempt_failure_detail ?? row.failure_detail),
    lastEventAt: nullableText(row.last_event_at),
  };
}

function mapWorker(row: RawRow): Worker {
  return {
    id: text(row.id),
    name: text(row.name),
    mode: text(row.mode, "pakistan-primary"),
    enabled: boolean(row.enabled),
    roles: stringArray<WorkerRole>(row.roles),
    reportedRoles: stringArray<WorkerRole>(row.reported_roles),
    build: nullableText(row.build),
    platform: nullableText(row.platform),
    architecture: nullableText(row.architecture),
    laneLimits: Object.fromEntries(
      Object.entries(object(row.lane_limits)).map(([key, value]) => [key, numeric(value)]),
    ),
    remoteRenderEnabled: boolean(row.remote_render_enabled),
    renderSlots: numeric(row.render_slots),
    storagePath: nullableText(row.storage_path),
    diskTotalBytes: row.disk_total_bytes === null ? null : numeric(row.disk_total_bytes),
    diskFreeBytes: row.disk_free_bytes === null ? null : numeric(row.disk_free_bytes),
    memoryTotalBytes: row.memory_total_bytes === null ? null : numeric(row.memory_total_bytes),
    memoryAvailableBytes:
      row.memory_available_bytes === null ? null : numeric(row.memory_available_bytes),
    memoryPressure: text(row.memory_pressure, "unknown"),
    swapUsedBytes: row.swap_used_bytes === null ? null : numeric(row.swap_used_bytes),
    swapDeltaBytes: row.swap_delta_bytes === null ? null : numeric(row.swap_delta_bytes),
    powerSource: text(row.power_source, "unknown"),
    batteryPercent: row.battery_percent === null ? null : numeric(row.battery_percent),
    batteryCharging: nullableBoolean(row.battery_charging),
    thermalState: text(row.thermal_state, "unknown"),
    thermalCelsius: row.thermal_celsius === null ? null : numeric(row.thermal_celsius),
    activeStage: nullableText(row.active_stage),
    activeRenderSubtaskId: nullableText(row.active_render_subtask_id),
    activeRenderAttemptId: nullableText(row.active_render_attempt_id),
    activeRenderStage: nullableText(row.active_render_stage),
    activeRenderProgress:
      row.active_render_progress === null ? null : numeric(row.active_render_progress),
    activeRenderStartedAt: nullableText(row.active_render_started_at),
    telemetryReportedAt: nullableText(row.telemetry_reported_at),
    health: text(row.health, "offline"),
    protocolVersion: nullableText(row.protocol_version),
    lastHeartbeatAt: nullableText(row.last_heartbeat_at),
    activeAttempts: numeric(row.active_attempts),
    activeRenderAttempts: numeric(row.active_render_attempts),
  };
}

function mapRenderSubtask(row: RawRow): RenderSubtask {
  return {
    id: text(row.id),
    jobId: text(row.job_id),
    jobTitle: text(row.job_title),
    ownerAttemptId: text(row.owner_attempt_id),
    ownerWorkerId: text(row.owner_worker_id),
    ownerWorkerName: text(row.owner_worker_name),
    status: text(row.status),
    manifestSha256: text(row.manifest_sha256),
    manifestByteCount: numeric(row.manifest_byte_count),
    failureCode: nullableText(row.failure_code),
    failureDetail: nullableText(row.failure_detail),
    createdAt: text(row.created_at),
    updatedAt: text(row.updated_at),
    completedAt: nullableText(row.completed_at),
    renderAttemptId: nullableText(row.render_attempt_id),
    attemptNumber: row.attempt_number === null ? null : numeric(row.attempt_number),
    renderWorkerId: nullableText(row.render_worker_id),
    renderWorkerName: nullableText(row.render_worker_name),
    renderAttemptStatus: nullableText(row.render_attempt_status),
    leaseExpiresAt: nullableText(row.lease_expires_at),
    progressSequence: numeric(row.progress_sequence),
    progress: object(row.progress) as RenderSubtask["progress"],
    outputSha256: nullableText(row.output_sha256),
    outputByteCount: row.output_byte_count === null ? null : numeric(row.output_byte_count),
    outputContentType: nullableText(row.output_content_type),
    cleanupStatus: nullableText(row.cleanup_status),
    cleanupRemovedBytes:
      row.cleanup_removed_bytes === null ? null : numeric(row.cleanup_removed_bytes),
    cleanupResidualBytes:
      row.cleanup_residual_bytes === null ? null : numeric(row.cleanup_residual_bytes),
    acceptedAt: nullableText(row.accepted_at),
    startedAt: nullableText(row.started_at),
    resultReceivedAt: nullableText(row.result_received_at),
    acknowledgedAt: nullableText(row.acknowledged_at),
    fencedAt: nullableText(row.fenced_at),
    cleanedAt: nullableText(row.cleaned_at),
    attemptFailureCode: nullableText(row.attempt_failure_code),
    attemptFailureDetail: nullableText(row.attempt_failure_detail),
    renderWorkerThermalState: nullableText(row.render_worker_thermal_state),
    renderWorkerThermalCelsius:
      row.render_worker_thermal_celsius === null
        ? null
        : numeric(row.render_worker_thermal_celsius),
    renderWorkerMemoryPressure: nullableText(row.render_worker_memory_pressure),
    renderWorkerSwapUsedBytes:
      row.render_worker_swap_used_bytes === null
        ? null
        : numeric(row.render_worker_swap_used_bytes),
    renderWorkerPowerSource: nullableText(row.render_worker_power_source),
    renderWorkerDiskFreeBytes:
      row.render_worker_disk_free_bytes === null
        ? null
        : numeric(row.render_worker_disk_free_bytes),
  };
}

function mapAccount(row: RawRow): AccountSummary {
  return {
    id: text(row.id),
    email: text(row.email),
    displayName: text(row.display_name),
    role: text(row.role) as AccountSummary["role"],
    mustChangePassword: boolean(row.must_change_password),
    totpEnabled: boolean(row.totp_enabled),
    disabledAt: nullableText(row.disabled_at),
    createdAt: text(row.created_at),
    updatedAt: text(row.updated_at),
  };
}

function mapAdminSession(row: RawRow): AdminSession {
  return {
    id: text(row.id),
    userId: text(row.user_id),
    email: text(row.email),
    displayName: text(row.display_name),
    role: text(row.role) as AdminSession["role"],
    ipAddress: nullableText(row.ip_address),
    userAgent: nullableText(row.user_agent),
    createdAt: text(row.created_at),
    lastSeenAt: text(row.last_seen_at),
    expiresAt: text(row.expires_at),
    current: boolean(row.is_current),
  };
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${encodeURIComponent(name)}=`;
  const part = document.cookie.split("; ").find((value) => value.startsWith(prefix));
  return part ? decodeURIComponent(part.slice(prefix.length)) : null;
}

function isMutation(method: string | undefined): boolean {
  return !["GET", "HEAD", "OPTIONS"].includes((method ?? "GET").toUpperCase());
}

export class PortalApiClient {
  private csrfToken: string | null = null;

  constructor(private readonly baseUrl = DEFAULT_BASE_URL) {}

  private rememberSession(payload: RawRow): Session {
    const session = mapSession(payload);
    this.csrfToken = session.csrfToken;
    return session;
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const headers = new Headers(options.headers);
    headers.set("Accept", "application/json");
    if (options.body !== undefined) headers.set("Content-Type", "application/json");
    if (options.idempotencyKey) headers.set("Idempotency-Key", options.idempotencyKey);
    if (options.csrf ?? isMutation(options.method)) {
      const csrf = this.csrfToken ?? readCookie(CSRF_COOKIE);
      if (csrf) headers.set("X-CSRF-Token", csrf);
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers,
      credentials: "include",
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });

    if (!response.ok) {
      let errorBody: ApiErrorBody = {};
      try {
        errorBody = (await response.json()) as ApiErrorBody;
      } catch {
        errorBody = {};
      }
      const serverError = errorBody.error;
      if (response.status === 401 && path !== "/auth/login") {
        window.dispatchEvent(new CustomEvent("swipecut:session-expired"));
      }
      throw new ApiError(
        response.status,
        serverError?.code ?? `HTTP_${response.status}`,
        serverError?.message ?? response.statusText ?? "Request failed",
        serverError?.details,
      );
    }

    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  session = {
    current: async () => this.rememberSession(await this.request<RawRow>("/auth/session")),
    login: async (email: string, password: string, totpCode?: string) =>
      this.rememberSession(
        await this.request<RawRow>("/auth/login", {
          method: "POST",
          body: { email, password, ...(totpCode ? { totpCode } : {}) },
          idempotencyKey: newIdempotencyKey(),
          csrf: false,
        }),
      ),
    logout: async () => {
      await this.request<{ loggedOut: true }>("/auth/logout", {
        method: "POST",
        idempotencyKey: newIdempotencyKey(),
      });
      this.csrfToken = null;
    },
    changePassword: async (currentPassword: string, newPassword: string) => {
      await this.request<{ changed: true }>("/auth/password", {
        method: "POST",
        body: { currentPassword, newPassword },
        idempotencyKey: newIdempotencyKey(),
      });
      return this.session.current();
    },
    beginTotp: () =>
      this.request<{ secret: string; uri: string }>("/auth/totp/begin", {
        method: "POST",
        idempotencyKey: newIdempotencyKey(),
      }),
    confirmTotp: async (code: string) => {
      const result = await this.request<{ enabled: true; recoveryCodes: string[] }>(
        "/auth/totp/confirm",
        {
          method: "POST",
          body: { code },
          idempotencyKey: newIdempotencyKey(),
        },
      );
      const session = await this.session.current();
      return { ...result, session };
    },
  };

  board = {
    get: async (): Promise<BoardResponse> => {
      const result = await this.request<{ items: RawRow[]; capabilities: BoardResponse["capabilities"] }>(
        "/board",
      );
      return { items: result.items.map(mapBoardItem), capabilities: result.capabilities };
    },
  };

  catalog = {
    get: async (): Promise<SettingsCatalog> => {
      const result = await this.request<{ channels: RawRow[]; profiles: RawRow[]; voices: RawRow[] }>(
        "/settings/catalog",
      );
      return {
        channels: result.channels.map(mapChannel),
        profiles: result.profiles.map(mapProfile),
        voices: result.voices.map(mapVoice),
      };
    },
    wordTargets: (minutes: number) =>
      this.request<WordTargets>(`/settings/word-targets/${minutes}`),
  };

  ideas = {
    create: async (input: IdeaInput) => {
      const result = await this.request<{ idea: RawRow }>("/ideas", {
        method: "POST",
        body: input,
        idempotencyKey: newIdempotencyKey(),
      });
      return mapBoardItem({ ...result.idea, type: "idea" });
    },
    bulk: async (items: IdeaInput[]) => {
      const result = await this.request<{ created: RawRow[]; duplicates: DuplicateIdea[] }>(
        "/ideas/bulk",
        {
          method: "POST",
          body: { items },
          idempotencyKey: newIdempotencyKey(),
        },
      );
      return {
        created: result.created.map((row) => mapBoardItem({ ...row, type: "idea" })),
        duplicates: result.duplicates,
      };
    },
    update: async (ideaId: string, input: Partial<IdeaInput>) => {
      const result = await this.request<{ idea: RawRow }>(`/ideas/${ideaId}`, {
        method: "PATCH",
        body: input,
        idempotencyKey: newIdempotencyKey(),
      });
      return mapBoardItem({ ...result.idea, type: "idea" });
    },
    approve: async (
      ideaId: string,
      input: {
        profileId: string;
        voiceId: string;
        durationMinutes: number;
        durationSource: "suggested" | "manager_override";
        publicationMode?: "normal" | "disabled_fixture";
      },
    ) => {
      const result = await this.request<{ job: RawRow }>(`/ideas/${ideaId}/approve`, {
        method: "POST",
        body: input,
        idempotencyKey: newIdempotencyKey(),
      });
      return mapJob(result.job);
    },
  };

  jobs = {
    list: async () => {
      const result = await this.request<{ jobs: RawRow[] }>("/jobs");
      return result.jobs.map(mapJob);
    },
    get: async (jobId: string) => {
      const jobs = await this.jobs.list();
      const job = jobs.find((item) => item.id === jobId);
      if (!job) throw new ApiError(404, "job_not_found", "Job not found");
      return job;
    },
    start: (jobIds: string[]) =>
      this.request<{ queued: string[] }>("/jobs/start", {
        method: "POST",
        body: { jobIds },
        idempotencyKey: newIdempotencyKey(),
      }),
    reorder: (jobIds: string[]) =>
      this.request<{ reordered: string[] }>("/jobs/reorder", {
        method: "POST",
        body: { jobIds },
        idempotencyKey: newIdempotencyKey(),
      }),
    pause: (reason: string) =>
      this.request<{ pauseRequested: true }>("/jobs/pause", {
        method: "POST",
        body: { reason },
        idempotencyKey: newIdempotencyKey(),
      }),
    retry: (jobId: string, reason: string) =>
      this.request<{ queued: true; attemptId: string }>(`/jobs/${jobId}/retry`, {
        method: "POST",
        body: { reason },
        idempotencyKey: newIdempotencyKey(),
      }),
    cancel: (jobId: string, reason: string) =>
      this.request<{ cancelled?: true; cancelRequested?: true }>(`/jobs/${jobId}/cancel`, {
        method: "POST",
        body: { reason },
        idempotencyKey: newIdempotencyKey(),
      }),
  };

  profiles = {
    create: async (input: { name: string; profile: Record<string, unknown>; isNeutral: boolean }) => {
      const result = await this.request<{ profile: RawRow }>("/profiles", {
        method: "POST",
        body: input,
        idempotencyKey: newIdempotencyKey(),
      });
      return mapProfile(result.profile);
    },
    update: async (
      profileId: string,
      input: Partial<{
        name: string;
        profile: Record<string, unknown>;
        isNeutral: boolean;
        active: boolean;
      }>,
    ) => {
      const result = await this.request<{ profile: RawRow }>(`/profiles/${profileId}`, {
        method: "PATCH",
        body: input,
        idempotencyKey: newIdempotencyKey(),
      });
      return mapProfile(result.profile);
    },
    export: (profileId: string) =>
      this.request<{
        name: string;
        version: number;
        profile: Record<string, unknown>;
        profile_hash: string;
        is_neutral: boolean;
      }>(`/profiles/${profileId}/export`),
  };

  channels = {
    updateDefaults: (
      channelId: string,
      input: {
        defaultProfileId: string;
        defaultVoiceId: string;
        defaultDurationMinutes: number;
      },
    ) =>
      this.request<{ channel: RawRow }>(`/channels/${channelId}/defaults`, {
        method: "PATCH",
        body: input,
        idempotencyKey: newIdempotencyKey(),
      }),
  };

  publications = {
    ready: async () => {
      const result = await this.request<{ jobs: RawRow[] }>("/publications/ready");
      return result.jobs.map(
        (row): PublicationJob => ({
          id: text(row.id),
          title: text(row.title),
          durationMinutes: numeric(row.duration_minutes),
          channelName: text(row.channel_name),
          driveUrl: text(row.drive_url),
          uploadedAt: nullableText(row.uploaded_at),
          createdAt: text(row.created_at),
          completedAt: nullableText(row.completed_at),
        }),
      );
    },
    publish: (jobId: string, youtubeUrl: string) =>
      this.request<{ published: true; youtubeUrl?: string }>(`/publications/${jobId}/published`, {
        method: "POST",
        body: { youtubeUrl },
        idempotencyKey: newIdempotencyKey(),
      }),
  };

  metrics = {
    throughput: async (): Promise<ThroughputMetrics> => {
      const result = await this.request<{
        throughput: RawRow[];
        queueDelay: RawRow[];
        stageDuration: RawRow[];
        failureRate: RawRow;
        utilization: RawRow[];
      }>("/metrics/throughput");
      return {
        throughput: result.throughput.map((row) => ({
          channelId: text(row.channel_id),
          channelName: text(row.channel_name),
          day: text(row.day),
          finishedVideos: numeric(row.finished_videos),
          finishedMinutes: numeric(row.finished_minutes),
          equivalent15MinuteVideos: numeric(row.equivalent_15_minute_videos),
        })),
        queueDelay: result.queueDelay.map((row) => ({
          channelId: text(row.channel_id),
          channelName: text(row.channel_name),
          averageQueueDelayMinutes: numeric(row.avg_queue_delay_minutes),
        })),
        stageDuration: result.stageDuration.map((row) => ({
          stage: text(row.stage),
          averageMinutes: numeric(row.avg_minutes),
        })),
        failureRate: {
          failedAttempts: numeric(result.failureRate.failed_attempts),
          totalAttempts: numeric(result.failureRate.total_attempts),
          failureRatePercent: numeric(result.failureRate.failure_rate_percent),
        },
        utilization: result.utilization.map((row) => ({
          id: text(row.id),
          name: text(row.name),
          health: text(row.health),
          lastHeartbeatAt: nullableText(row.last_heartbeat_at),
          activeAttempts: numeric(row.active_attempts),
          configuredSlots: numeric(row.configured_slots),
        })),
      };
    },
  };

  admin = {
    users: async () => {
      const result = await this.request<{ users: RawRow[] }>("/admin/users");
      return result.users.map(mapAccount);
    },
    sessions: async () => {
      const result = await this.request<{ sessions: RawRow[] }>("/admin/sessions");
      return result.sessions.map(mapAdminSession);
    },
    revokeSession: (sessionId: string) =>
      this.request<{ revoked: true }>(`/admin/sessions/${sessionId}/revoke`, {
        method: "POST",
        idempotencyKey: newIdempotencyKey(),
      }),
    resetPassword: (userId: string) =>
      this.request<{ email: string; oneTimePassword: string }>(
        `/admin/users/${userId}/reset-password`,
        {
          method: "POST",
          idempotencyKey: newIdempotencyKey(),
        },
      ),
    workers: async () => {
      const result = await this.request<{ workers: RawRow[] }>("/admin/workers");
      return result.workers.map(mapWorker);
    },
    renderSubtasks: async (limit = 100) => {
      const result = await this.request<{ renderSubtasks: RawRow[] }>(
        `/admin/render-subtasks?limit=${encodeURIComponent(String(limit))}`,
      );
      return result.renderSubtasks.map(mapRenderSubtask);
    },
    updateWorker: async (
      workerId: string,
      input: { enabled?: boolean; authorizedRoles?: WorkerRole[] },
    ) => {
      const result = await this.request<{ worker: RawRow }>(`/admin/workers/${workerId}`, {
        method: "PATCH",
        body: input,
        idempotencyKey: newIdempotencyKey(),
      });
      return mapWorker(result.worker);
    },
    config: async (): Promise<PortalConfig> => {
      const result = await this.request<{ config: RawRow[] }>("/admin/config");
      const values = new Map(result.config.map((row) => [text(row.key), row]));
      const read = (key: string) => object(values.get(key));
      return {
        canaryMode: boolean(read("canary_mode").value),
        trelloBridgeEnabled: boolean(read("trello_bridge_enabled").value),
        normalBatchesEnabled: boolean(read("normal_batches_enabled").value),
        updatedAt:
          nullableText(read("canary_mode").updated_at) ??
          nullableText(read("trello_bridge_enabled").updated_at) ??
          nullableText(read("normal_batches_enabled").updated_at),
      };
    },
    updateConfig: (input: Partial<Omit<PortalConfig, "updatedAt">>) =>
      this.request<{ updated: true }>("/admin/config", {
        method: "PATCH",
        body: input,
        idempotencyKey: newIdempotencyKey(),
      }),
    audit: async (cursor?: string, limit = 50) => {
      const query = new URLSearchParams({ limit: String(limit) });
      if (cursor) query.set("cursor", cursor);
      const result = await this.request<{ records: RawRow[]; nextCursor: string | null }>(
        `/admin/audit?${query}`,
      );
      return {
        records: result.records.map(
          (row): AuditRecord => ({
            id: text(row.id),
            actorType: text(row.actor_type),
            actorId: nullableText(row.actor_id),
            action: text(row.action),
            entityType: nullableText(row.entity_type),
            entityId: nullableText(row.entity_id),
            beforeState: row.before_state,
            afterState: row.after_state,
            createdAt: text(row.created_at),
          }),
        ),
        nextCursor: result.nextCursor,
      };
    },
  };
}

export const portalApi = new PortalApiClient();
